import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, BugReporterProvider } from '@clareefai/ui';
import { store } from './store';
import { AuthProvider } from './contexts/AuthContext';
import { AuthDataSync } from './contexts/AuthDataSync';
import { AbilityProviderWrapper } from './contexts/AbilityProviderWrapper';
import PrivateRoute from './components/shared/PrivateRoute';
import PermissionGuard from './components/shared/PermissionGuard';
import Layout from './components/shared/Layout';
import Login from './pages/Login';
import McpConsent from './pages/oauth/McpConsent';
import ForgotPassword from './pages/ForgotPassword';
import CreateClareefaiPage from './pages/CreateClareefAiPage';
import SetPassword from './pages/auth/SetPassword';
import Dashboard from './pages/dashboard/Dashboard';
import Meetings from './pages/meetings/Meetings';
import Promoters from './pages/promoters/Promoters';

import PromoterDetail from './pages/promoters/PromoterDetail';
import AddPromoter from './pages/promoters/AddPromoter';
import CRMImport from './pages/promoters/CRMImport';
import HubSpotCallback from './pages/promoters/HubSpotCallback';
import Prospects from './pages/prospects/Prospects';

import ProspectDetail from './pages/prospects/ProspectDetail';
import AddProspect from './pages/prospects/AddProspect';
import CreatePage from './pages/vendor-page/CreatePage';
import MyPage from './pages/vendor-page/MyPage';
import Rewards from './pages/rewards/Rewards';
import AddSpecificReward from './pages/rewards/AddSpecificReward';
import CreateCustomReward from './pages/rewards/CreateCustomReward';
import SpecificRewardDetail from './pages/rewards/SpecificRewardDetail';
import ReviewsManagement from './pages/promoters/ReviewsManagement';
import ReviewDetail from './pages/promoters/ReviewDetail';
import Testimonials from './pages/promoters/Testimonials';
import RequestTestimonial from './pages/promoters/RequestTestimonial';
import TestimonialDetails from './pages/promoters/TestimonialDetails';
import UseCases from './pages/promoters/UseCases';
import Referrals from './pages/promoters/Referrals';
import ReferralDetail from './pages/promoters/ReferralDetail';
import SurveyDashboard from './pages/promoters/SurveyDashboard';
import SurveyDetail from './pages/promoters/SurveyDetail';
import SurveyQuestions from './pages/promoters/SurveyQuestions';
import SendSurvey from './pages/promoters/SendSurvey';
import SurveyResponseDetail from './pages/promoters/SurveyResponseDetail';
import RequestUseCase from './pages/promoters/RequestUseCase';
import PublishedUseCaseDetails from './pages/promoters/PublishedUseCaseDetails';
import UseCaseSubmissionDetail from './pages/promoters/UseCaseSubmissionDetail';
import TestimonialRequestDetail from './pages/promoters/TestimonialRequestDetail';
import OneToOneCalls from './pages/promoters/OneToOneCalls';
import OneToOneCallRequestDetails from './pages/promoters/OneToOneCallRequestDetails';
import OneToOneCallDetails from './pages/promoters/OneToOneCallDetails';
import Request1to1Call from './pages/promoters/Request1to1Call';
import VIPRequests from './pages/promoters/VIPRequests';
import PublicPreview from './pages/PublicPreview';
import PublicUseCaseRedirect from './pages/PublicUseCaseRedirect';
import PublicReview from './pages/PublicReview';
import ReviewRedirect from './pages/ReviewRedirect';
import TeamsSales from './pages/teams/TeamsSales';
import TeamMemberDetail from './pages/teams/TeamMemberDetail';
import AddTeamMember from './pages/teams/AddTeamMember';
import EditTeamMember from './pages/teams/EditTeamMember';
import TeamMemberSettings from './pages/team-settings/TeamMemberSettings';
import TeamProfileTab from './pages/team-settings/tabs/TeamProfileTab';
import TeamProductsTab from './pages/team-settings/tabs/TeamProductsTab';
import TeamNotificationsTab from './pages/team-settings/tabs/TeamNotificationsTab';
import TeamEmailSenderTab from './pages/team-settings/tabs/TeamEmailSenderTab';
import TeamEmailIntegrationCallback from './pages/team-settings/TeamEmailIntegrationCallback';
import MyAssignments from './pages/teams/MyAssignments';
import MySubscription from './pages/subscription/MySubscription';
import BillingManagement from './pages/subscription/BillingManagement';
import Settings from './pages/settings/Settings';
import NotificationSettings from './pages/settings/NotificationSettings';
import EmailTemplates from './pages/settings/EmailTemplates';
import UseCaseQuestionsSettings from './pages/settings/UseCaseQuestionsSettings';
import ReviewQuestionsSettings from './pages/settings/ReviewQuestionsSettings';
import EmailIntegrationSettings from './pages/settings/EmailIntegrationSettings';
import AgentAccessSettings from './pages/settings/AgentAccessSettings';
import ConnectAgentGuide from './pages/settings/ConnectAgentGuide';
import WidgetSettings from './pages/settings/WidgetSettings';
import WidgetManagement from './pages/widget/WidgetManagement';
import EmailIntegrationCallback from './pages/settings/EmailIntegrationCallback';
import TechnicalDocumentation from './pages/settings/TechnicalDocumentation';
import DataModel from './pages/settings/DataModel';
import Onboarding from './pages/Onboarding';
import TeamMemberOnboarding from './pages/auth/TeamMemberOnboarding';
import DefaultRedirect from './components/shared/DefaultRedirect';
import OnboardingGuard from './components/shared/OnboardingGuard';
import Documentation from './pages/public/Documentation';
import Support from './pages/public/Support';
import ImpersonateExchange from './pages/ImpersonateExchange';
import TrialEntryPage from './pages/trial/TrialEntryPage';
import TrialSignupPage from './pages/trial/TrialSignupPage';
import TrialVerifyEmailPage from './pages/trial/TrialVerifyEmailPage';
import DealRooms from './pages/deal-rooms/DealRooms';
import DealRoomEditor from './pages/deal-rooms/DealRoomEditor';

function App() {
  return (
    <AuthProvider>
      <AuthDataSync>
        <AbilityProviderWrapper>
          <Toaster />
          <Router>
          <BugReporterProvider appName="vendor" store={store}>
          <Sentry.ErrorBoundary fallback={({ resetError }) => (
            <div className="flex flex-col items-center justify-center py-20">
              <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
              <p className="text-gray-500 mb-4">This page encountered an error.</p>
              <button onClick={resetError} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                Try Again
              </button>
            </div>
          )}>
          <Routes>
            <Route path="/start" element={<TrialEntryPage />} />
            <Route path="/trial/signup" element={<TrialSignupPage />} />
            <Route path="/trial/verify-email" element={<TrialVerifyEmailPage />} />
            <Route path="/login" element={<Login />} />
            {/* MCP OAuth consent handoff (docs/MCP_SERVER_PLAN.md D9). Standalone page —
                handles its own auth (bounces through /login, preserving request_id). */}
            <Route path="/oauth/consent" element={<McpConsent />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-magic-link" element={<SetPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/team-member-onboarding" element={<TeamMemberOnboarding />} />
            <Route path="/create-clareefai-page" element={<CreateClareefaiPage />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/public-preview" element={<PublicPreview />} />
            <Route path="/company/:slug" element={<PublicPreview />} />
            <Route path="/public-use-case/:id" element={<PublicUseCaseRedirect />} />
            <Route path="/review/:id" element={<ReviewRedirect />} />
            <Route path="/docs" element={<Documentation />} />
            <Route path="/support" element={<Support />} />
            <Route path="/impersonate" element={<ImpersonateExchange />} />
            <Route path="/" element={<DefaultRedirect />} />
            
            <Route path="/" element={
              <PrivateRoute>
                <OnboardingGuard>
                  <Layout />
                </OnboardingGuard>
              </PrivateRoute>
            }>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="meetings" element={<Meetings />} />
              
              <Route path="promoters" element={<Promoters />} />
              <Route path="promoters/:id" element={<PromoterDetail />} />
              <Route path="add-promoter" element={<PermissionGuard action="create" subject="Promoter" fallback="/promoters"><AddPromoter /></PermissionGuard>} />
              <Route path="crm-import" element={<PermissionGuard action="access" subject="CrmImport" fallback="/promoters"><CRMImport /></PermissionGuard>} />
              <Route path="crm-import/hubspot-callback" element={<PermissionGuard action="access" subject="CrmImport" fallback="/promoters"><HubSpotCallback /></PermissionGuard>} />

              <Route path="prospects" element={<Prospects />} />
              <Route path="add-prospect" element={<PermissionGuard action="create" subject="Prospect" fallback="/prospects"><AddProspect /></PermissionGuard>} />
              <Route path="prospect/:id" element={<ProspectDetail />} />
              <Route path="create-page" element={<PermissionGuard action="update" subject="Page"><CreatePage /></PermissionGuard>} />
              <Route path="my-page" element={<MyPage />} />
              <Route path="rewards" element={<PermissionGuard action="read" subject="Reward"><Rewards /></PermissionGuard>} />
              <Route path="rewards/add-specific" element={<PermissionGuard action="create" subject="Reward"><AddSpecificReward /></PermissionGuard>} />
              <Route path="rewards/create-custom" element={<PermissionGuard action="create" subject="Reward"><CreateCustomReward /></PermissionGuard>} />
              <Route path="rewards/specific/:id" element={<PermissionGuard action="read" subject="Reward"><SpecificRewardDetail /></PermissionGuard>} />
              <Route path="reviews" element={<ReviewsManagement />} />
              <Route path="reviews/:id" element={<ReviewDetail />} />
              <Route path="testimonials" element={<Testimonials />} />
              <Route path="testimonials/request" element={<RequestTestimonial />} />
              <Route path="testimonials/:id" element={<TestimonialDetails />} />
              <Route path="use-cases" element={<UseCases />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="referrals/:id" element={<ReferralDetail />} />
              <Route path="survey" element={<SurveyDashboard />} />
              <Route path="survey/:surveyId" element={<SurveyDetail />} />
              <Route path="survey/questions/:surveyId" element={<SurveyQuestions />} />
              <Route path="survey/send/:surveyId" element={<SendSurvey />} />
              <Route path="survey/response/:responseId" element={<SurveyResponseDetail />} />
              <Route path="request-use-case" element={<RequestUseCase />} />
              <Route path="testimonial-requests/:id" element={<TestimonialRequestDetail />} />
              <Route path="use-cases/published/:id" element={<PublishedUseCaseDetails />} />
              <Route path="use-case-submission/:id" element={<UseCaseSubmissionDetail />} />
              <Route path="1to1-calls" element={<OneToOneCalls />} />
              <Route path="1to1-calls/request/:id" element={<OneToOneCallRequestDetails />} />
              <Route path="1to1-calls/details/:id" element={<OneToOneCallDetails />} />
              <Route path="request-1to1-call" element={<Request1to1Call />} />
              <Route path="vip-requests" element={<VIPRequests />} />
              <Route path="teams-sales" element={<PermissionGuard action="read" subject="TeamMember" fallback="/dashboard"><TeamsSales /></PermissionGuard>} />
              <Route path="team-member/:memberId" element={<PermissionGuard action="update" subject="TeamMember" fallback="/teams-sales"><TeamMemberDetail /></PermissionGuard>} />
              <Route path="team-member/:memberId/edit" element={<PermissionGuard action="update" subject="TeamMember" fallback="/teams-sales"><EditTeamMember /></PermissionGuard>} />
              <Route path="add-team-member" element={<PermissionGuard action="create" subject="TeamMember" fallback="/teams-sales"><AddTeamMember /></PermissionGuard>} />
              <Route path="team-profile" element={<Navigate to="/team-settings/profile" replace />} />
              <Route path="team-settings" element={<PermissionGuard teamMemberOnly fallback="/dashboard"><TeamMemberSettings /></PermissionGuard>}>
                <Route index element={<Navigate to="profile" replace />} />
                <Route path="profile" element={<TeamProfileTab />} />
                <Route path="products" element={<TeamProductsTab />} />
                <Route path="notifications" element={<TeamNotificationsTab />} />
                <Route path="email-sender" element={<TeamEmailSenderTab />} />
              </Route>
              <Route path="team-settings/email-sender/:provider/callback" element={<TeamEmailIntegrationCallback />} />
              <Route path="my-assignments" element={<PermissionGuard teamMemberOnly fallback="/dashboard"><MyAssignments /></PermissionGuard>} />
              <Route path="my-subscription" element={<PermissionGuard vendorOnly fallback="/dashboard"><MySubscription /></PermissionGuard>} />
              <Route path="billing-management" element={<PermissionGuard vendorOnly fallback="/dashboard"><BillingManagement /></PermissionGuard>} />
              <Route path="profile" element={<Navigate to="/settings" replace />} />
              <Route path="vendor/profile" element={<Navigate to="/settings" replace />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/notifications" element={<NotificationSettings />} />
              <Route path="settings/email-templates" element={<PermissionGuard action="manage" subject="EmailTemplate" fallback="/settings"><EmailTemplates /></PermissionGuard>} />
              <Route path="settings/use-case-questions" element={<PermissionGuard action="update" subject="UseCase" fallback="/settings"><UseCaseQuestionsSettings /></PermissionGuard>} />
              <Route path="settings/review-questions" element={<PermissionGuard action="update" subject="UseCase" fallback="/settings"><ReviewQuestionsSettings /></PermissionGuard>} />
              <Route path="settings/email-integration" element={<EmailIntegrationSettings />} />
              <Route path="settings/agent-access" element={<PermissionGuard action="manage" subject="AgentAccess" fallback="/settings"><AgentAccessSettings /></PermissionGuard>} />
              <Route path="settings/agent-access/guide" element={<PermissionGuard action="manage" subject="AgentAccess" fallback="/settings"><ConnectAgentGuide /></PermissionGuard>} />
              <Route path="widget-management" element={<WidgetManagement />} />
              <Route path="deal-rooms" element={<DealRooms />} />
              <Route path="deal-rooms/new" element={<DealRoomEditor />} />
              <Route path="deal-rooms/:id" element={<DealRoomEditor />} />
              {/* Back-compat: the widget settings moved to the Widget Management hub. */}
              <Route path="settings/widget" element={<Navigate to="/widget-management" replace />} />
              <Route path="settings/email-integration/:provider/callback" element={<EmailIntegrationCallback />} />
              <Route path="technical-documentation" element={<TechnicalDocumentation />} />
              <Route path="data-model" element={<DataModel />} />
            </Route>

            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
                <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Page not found</h1>
                  <p className="text-gray-600 mb-6">
                    The link you followed may be broken or expired. If you were setting up your
                    account, please contact <a href="mailto:support@clareefai.com" className="text-purple-600 font-medium">support@clareefai.com</a>.
                  </p>
                  <a href="/login" className="btn-primary inline-flex items-center">Go to login</a>
                </div>
              </div>
            } />
          </Routes>
          </Sentry.ErrorBoundary>
          </BugReporterProvider>
          </Router>
        </AbilityProviderWrapper>
      </AuthDataSync>
    </AuthProvider>
  );
}

export default App;